let WebSocketNotificationManager = null

try {
  if (typeof require !== 'undefined') {
    const managerModule = require('./frontend-websocket-manager.js')
    WebSocketNotificationManager = managerModule.default || managerModule
  }
} catch (error) {
  // ignore, will try window fallback
}

if (!WebSocketNotificationManager && typeof window !== 'undefined') {
  WebSocketNotificationManager = window.WebSocketNotificationManager || null
}

if (!WebSocketNotificationManager) {
  throw new Error('WebSocketNotificationManager 未找到，请确保先引入对应脚本')
}

/**
 * 通知方式切换器（目前仅支持WebSocket）
 * 负责统一管理通知连接、事件转发等逻辑
 */
class NotificationSwitcher {
  constructor(options = {}) {
    this.baseUrl = options.baseUrl || 'auto'
    this.userId = options.userId
    this.currentMethod = 'websocket'
    this.listeners = new Map()

    this.webSocketManager = null
    this.webSocketEventsBound = false
    this.heartbeatPayload = options.heartbeatPayload !== undefined ? options.heartbeatPayload : 'ping'
    this.heartbeatInterval = options.heartbeatInterval || 25000
    this.heartbeatTimeout = options.heartbeatTimeout || 60000
    this.maxReconnectDelay = options.maxReconnectDelay || 30000
    this.baseReconnectDelay = options.baseReconnectDelay || 1000
  }

  /**
   * 建立连接
   */
  connect(userId = this.userId) {
    if (!userId) {
      console.warn('NotificationSwitcher.connect: 用户ID不能为空')
      return Promise.resolve({ error: '用户ID不能为空' })
    }

    this.userId = userId
    const manager = this.ensureWebSocketManager(userId)

    const status = manager.getStatus()
    if (status.isConnected && manager.websocket && manager.websocket.readyState === WebSocket.OPEN) {
      return Promise.resolve({ method: 'websocket', ...status })
    }

    return manager.connect(userId).then(result => {
      this.emit('connected', { method: 'websocket', ...result })
      return result
    }).catch(error => {
      console.warn('NotificationSwitcher连接失败，将静默重连:', error)
      return { error: error.message || '连接失败，将静默重连' }
    })
  }

  /**
   * 断开连接
   */
  disconnect() {
    if (this.webSocketManager) {
      this.webSocketManager.disconnect()
    }
    this.emit('disconnected', { method: 'websocket' })
  }

  /**
   * 切换通知方式（目前仅保留WebSocket）
   */
  switchMethod(method, userId = this.userId) {
    if (method !== 'websocket') {
      console.warn(`当前仅支持 webSocket，忽略切换请求: ${method}`)
      return Promise.resolve()
    }

    this.currentMethod = 'websocket'
    return this.connect(userId)
  }

  /**
   * 发送消息
   */
  sendMessage(message) {
    if (!this.webSocketManager) {
      console.warn('WebSocket尚未初始化，无法发送消息')
      return false
    }
    return this.webSocketManager.sendMessage(message)
  }

  /**
   * 获取当前状态
   */
  getStatus() {
    const baseStatus = {
      currentMethod: 'websocket',
      userId: this.userId
    }

    if (this.webSocketManager) {
      return { ...baseStatus, ...this.webSocketManager.getStatus() }
    }

    return {
      ...baseStatus,
      isConnected: false,
      readyState: null,
      reconnectAttempts: 0,
      url: null
    }
  }

  /**
   * 获取统计信息
   */
  getStats() {
    return {
      currentMethod: 'websocket',
      websocket: this.webSocketManager ? this.webSocketManager.getStatus() : null
    }
  }

  /**
   * 检查连接状态
   */
  checkConnection() {
    if (this.webSocketManager) {
      return this.webSocketManager.checkConnection()
    }
    return false
  }

  /**
   * 注册事件监听器
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, [])
    }
    this.listeners.get(event).push(callback)
  }

  /**
   * 移除事件监听器
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event)
      const index = callbacks.indexOf(callback)
      if (index > -1) {
        callbacks.splice(index, 1)
      }
    }
  }

  /**
   * 触发事件
   */
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error('事件回调执行失败:', error)
        }
      })
    }
  }

  /**
   * 确保WebSocket管理器存在并绑定事件
   */
  ensureWebSocketManager(userId) {
    if (!this.webSocketManager) {
      this.webSocketManager = new WebSocketNotificationManager({
        baseUrl: this.baseUrl,
        userId,
        heartbeatPayload: this.heartbeatPayload,
        heartbeatInterval: this.heartbeatInterval,
        heartbeatTimeout: this.heartbeatTimeout,
        maxReconnectDelay: this.maxReconnectDelay,
        baseReconnectDelay: this.baseReconnectDelay
      })
      this.webSocketEventsBound = false
    } else {
      if (this.webSocketManager.rawBaseUrl !== this.baseUrl) {
        this.webSocketManager.rawBaseUrl = this.baseUrl
        this.webSocketManager.baseUrl = null
      }

      if (this.webSocketManager.heartbeatPayload !== this.heartbeatPayload) {
        this.webSocketManager.heartbeatPayload = this.heartbeatPayload
      }

      if (this.webSocketManager.heartbeatIntervalMs !== this.heartbeatInterval) {
        this.webSocketManager.heartbeatIntervalMs = this.heartbeatInterval
      }

      if (this.webSocketManager.heartbeatTimeout !== this.heartbeatTimeout) {
        this.webSocketManager.heartbeatTimeout = this.heartbeatTimeout
      }

      if (this.webSocketManager.maxReconnectDelay !== this.maxReconnectDelay) {
        this.webSocketManager.maxReconnectDelay = this.maxReconnectDelay
      }

      if (this.webSocketManager.baseReconnectDelay !== this.baseReconnectDelay) {
        this.webSocketManager.baseReconnectDelay = this.baseReconnectDelay
      }

      if (userId && this.webSocketManager.userId !== userId) {
        this.webSocketManager.userId = userId
      }
    }

    this.bindWebSocketEvents()
    return this.webSocketManager
  }

  /**
   * 绑定WebSocket事件到切换器
   */
  bindWebSocketEvents() {
    if (!this.webSocketManager || this.webSocketEventsBound) {
      return
    }

    const forward = (eventName) => (payload) => {
      this.emit(eventName, payload)
    }

    this.webSocketManager.on('connected', (payload) => {
      this.emit('connected', { method: 'websocket', ...payload })
    })

    this.webSocketManager.on('disconnected', forward('disconnected'))
    this.webSocketManager.on('notification', forward('notification'))
    this.webSocketManager.on('heartbeat', forward('heartbeat'))
    this.webSocketManager.on('message', forward('message'))
    this.webSocketManager.on('error', forward('error'))
    this.webSocketManager.on('reconnectFailed', forward('reconnectFailed'))

    this.webSocketEventsBound = true
  }
}

if (typeof window !== 'undefined') {
  window.NotificationSwitcher = NotificationSwitcher
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = NotificationSwitcher
}
